import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";
import { Sparkles } from "lucide-react";
import { AgentOrb } from "@/components/ui/agent-orb";
import { toast } from "sonner";
import { useBoundStore } from "../../store";
import { useI18n } from "../../i18n";

interface CreatePlaybookAIDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function CreatePlaybookAIDialog({ open, onOpenChange }: CreatePlaybookAIDialogProps) {
  const navigate = useNavigate();
  const { t } = useI18n();
  const createPlaybookWithAI = useBoundStore((state) => state.createPlaybookWithAI);
  const isLoading = useBoundStore((state) => state.isLoading);

  // AI playbook generation typically takes 10-25s. Backend doesn't stream
  // progress, so the bar is purely cosmetic - it asymptotes toward 95% so the
  // user feels something is happening, then snaps to 100% when the response
  // arrives.
  const STAGES = [
    t.playbooks.aiDialog.stages.reading,
    t.playbooks.aiDialog.stages.identifying,
    t.playbooks.aiDialog.stages.drafting,
    t.playbooks.aiDialog.stages.refining,
    t.playbooks.aiDialog.stages.almostDone,
  ];
  const stageCount = STAGES.length;

  const [userPrompt, setUserPrompt] = useState("");
  const [isPrivate, setIsPrivate] = useState(true);
  const [progress, setProgress] = useState(0);
  const [stageIndex, setStageIndex] = useState(0);

  // Drive the cosmetic progress bar + stage label while isLoading.
  useEffect(() => {
    if (!isLoading) {
      setProgress(0);
      setStageIndex(0);
      return;
    }
    setProgress(5);
    setStageIndex(0);
    const tick = setInterval(() => {
      setProgress((p) => (p < 95 ? p + (100 - p) * 0.04 : p));
    }, 400);
    const stage = setInterval(() => {
      setStageIndex((i) => Math.min(stageCount - 1, i + 1));
    }, 4000);
    return () => {
      clearInterval(tick);
      clearInterval(stage);
    };
  }, [isLoading, stageCount]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!userPrompt.trim()) {
      toast.error(t.playbooks.aiDialog.describeRequired);
      return;
    }

    const playbook = await createPlaybookWithAI({
      userPrompt: userPrompt.trim(),
      isPrivate,
    });

    if (playbook) {
      toast.success(t.playbooks.aiDialog.createSuccess);
      // Reset form
      setUserPrompt("");
      setIsPrivate(true);
      onOpenChange(false);
      // Navigate to the created playbook
      navigate(`/playbooks/${playbook.id}`);
    } else {
      const error = useBoundStore.getState().error;
      toast.error(error || t.playbooks.aiDialog.createFailed);
    }
  };

  const handleCancel = () => {
    setUserPrompt("");
    setIsPrivate(true);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5" />
              {t.playbooks.aiDialog.title}
            </DialogTitle>
            <DialogDescription>
              {t.playbooks.aiDialog.description}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            {isLoading ? (
              <div className="rounded-lg border bg-muted/30 px-6 py-8">
                <div role="status" aria-live="polite" className="flex items-center gap-3">
                  <AgentOrb state="composing" />
                  <p className="font-medium">{STAGES[stageIndex]}...</p>
                </div>
                <Progress value={progress} className="mt-4 h-2" />
                <p className="mt-3 text-xs text-muted-foreground">
                  {t.playbooks.aiDialog.timeHint}
                </p>
              </div>
            ) : (
              <>
                <div className="grid gap-2">
                  <Label htmlFor="userPrompt">
                    {t.playbooks.aiDialog.descriptionLabel} <span className="text-red-500">*</span>
                  </Label>
                  <Textarea
                    id="userPrompt"
                    value={userPrompt}
                    onChange={(e) => setUserPrompt(e.target.value)}
                    placeholder={t.playbooks.aiDialog.descriptionPlaceholder}
                    rows={6}
                    required
                  />
                  <p className="text-xs text-muted-foreground">
                    {t.playbooks.aiDialog.descriptionHint}
                  </p>
                </div>

                <div className="flex items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <Label htmlFor="isPrivate">{t.playbooks.aiDialog.privateLabel}</Label>
                    <p className="text-sm text-muted-foreground">
                      {isPrivate
                        ? t.playbooks.aiDialog.privateOnlyYou
                        : t.playbooks.aiDialog.privateEveryone}
                    </p>
                  </div>
                  <Switch id="isPrivate" checked={isPrivate} onCheckedChange={setIsPrivate} />
                </div>
              </>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleCancel} disabled={isLoading}>
              {t.playbooks.aiDialog.cancel}
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? (
                <>
                  <Sparkles className="mr-2 h-4 w-4 animate-pulse" />
                  {t.playbooks.aiDialog.generating}
                </>
              ) : (
                <>
                  <Sparkles className="mr-2 h-4 w-4" />
                  {t.playbooks.aiDialog.submit}
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
